document.addEventListener("DOMContentLoaded", async () => {
  const ddContainer = document.querySelector(".pc-pro-dropdown-container");
  const ddContent = document.querySelector(".pc-pro-dropdown-content");
  const collectionHandle = ddContent.dataset.collectionHandle;
  const bestSellersTitle = ddContent.dataset.bestSellersTitle;
  const makesTitle = ddContent.dataset.makesTitle;
  const noVehicleFoundText = ddContent.dataset.noVehicleFound;
  // Optional: add data-loading-text in liquid to localize this.
  const loadingText = ddContent.dataset.loadingText || "Loading…";
  const ddList = document.querySelector(".pc-pro-dd-list");
  const ddSelections = document.querySelector(".pc-pro-selection-container");
  const ddButton = document.querySelector(".dropdown-button");

  // Show a loading state (and hide the dropdown UI) until data is ready.
  injectPcProStyles();
  showInitialLoading();

  const makeModels = await getMakeModels();
  const bestSellerMakes = ddContent.dataset.bestSellers?.split(",") ?? [];
  const filteredMakes = Object.keys(makeModels).filter(
    (make) => !bestSellerMakes.includes(make)
  );

  // Available PC codes coming from the "pedal-commander-generic-product"
  // (low_product). Only model variants whose PC code is in this allowlist
  // will be shown. Source: data-available-pc-codes (set in liquid).
  const availablePcCodesRaw = ddContent.dataset.availablePcCodes;
  const availablePcCodes = new Set(
    (availablePcCodesRaw || "")
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean)
  );

  // Anti-theft socket PC codes (metaobject pedal-commander-pro-sockets).
  // This dropdown is for the "without anti-theft" collection, so any variant
  // whose cleaned PC code is in this set is excluded.
  const proSockets = new Set(
    (ddContent.dataset.proSockets || "")
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean)
  );

  // Guards against stale variant fetches when the user navigates quickly.
  let activeVariantRequest = 0;

  getInitialDropdown({ addSearchBar: true });
  markReady();

  ddButton.addEventListener("click", handleDropdownButtonClick);

  // FUNCTIONS START

  function handleKeydown(e) {
    const highlightedElement = ddContent.querySelector(
      ".pc-pro-dd-list-item.highlight"
    );
    const focusableElements = ddContent.querySelectorAll(
      ".pc-pro-dd-list-item:not(.pc-pro-dd-search-bar-li):not(.pc-pro-dd-list-splitter):not(.no-hover)"
    );
    switch (e.key) {
      case "Escape":
        closeDropdown();
        highlightedElement?.classList.remove("highlight");
        break;
      case "ArrowDown": {
        e.preventDefault();
        ddContent.dataset.keyboardControls = "true";
        const nextElement = highlightedElement?.nextElementSibling;
        if (!nextElement) {
          const elementToFocus = focusableElements[0];
          elementToFocus.classList.add("highlight");
          centerElementInContainer(elementToFocus, ddList);
        } else {
          highlightedElement.classList.remove("highlight");
          nextElement.classList.add("highlight");
          centerElementInContainer(nextElement, ddList);
        }
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        ddContent.dataset.keyboardControls = "true";
        const previousElement = highlightedElement?.previousElementSibling;
        if (!previousElement) {
          const elementToFocus =
            focusableElements[focusableElements.length - 1];
          elementToFocus.classList.add("highlight");
          centerElementInContainer(elementToFocus, ddList);
        } else {
          highlightedElement.classList.remove("highlight");
          previousElement.classList.add("highlight");
          centerElementInContainer(previousElement, ddList);
        }
        break;
      }
      case "Enter":
        e.preventDefault();
        highlightedElement?.click();
        break;
    }
  }

  function centerElementInContainer(element, container) {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const offset =
      elementRect.top -
      containerRect.top -
      containerRect.height / 2 +
      elementRect.height / 2;
    container.scrollTop += offset;
  }

  function getInitialDropdown({ addSearchBar }) {
    if (addSearchBar) {
      appendSearchBar();
    }
    setSearchBarVisible(true);
    ddList.style.height = "";
    const bestSellersSplitter = getSplitter({ text: bestSellersTitle });
    ddList.append(bestSellersSplitter);
    fillListMakes(bestSellerMakes);

    const makesSplitter = getSplitter({ text: makesTitle });
    ddList.append(makesSplitter);
    fillListMakes(filteredMakes);
  }

  function handleDropdownButtonClick() {
    const isOpened = ddContent.dataset.dropdownOpened === "true";
    if (isOpened) {
      closeDropdown();
    } else {
      openDropdown();
    }
  }

  function openDropdown() {
    ddContent.dataset.dropdownOpened = true;
    ddContent.querySelector(".pc-pro-search-bar")?.focus();
    window.addEventListener("keydown", handleKeydown);
    window.addEventListener("click", handleDocumentClick);
  }

  function closeDropdown() {
    ddContent.dataset.dropdownOpened = false;
    ddContent.dataset.keyboardControls = "false";
    window.removeEventListener("keydown", handleKeydown);
    window.removeEventListener("click", handleDocumentClick);
  }

  function handleDocumentClick(e) {
    const closestDropdownContent = e.target.closest(".pc-pro-dropdown-content");
    if (
      !closestDropdownContent &&
      !e.target.classList.contains("pc-pro-dd-list-item")
    ) {
      closeDropdown();
    }
  }

  function fillListMakes(items) {
    items.forEach((item) => {
      const liElement = getMakeListItem({ make: item });
      ddList.append(liElement);
    });
  }

  function fillListModels(make) {
    const models = makeModels[make];
    clearDD();
    setSearchBarVisible(true);
    models.forEach((model) => {
      const liElement = getModelListItem({
        label: model,
        handle: toHandle(`${make} ${model}`),
        make,
        model,
      });
      ddList.append(liElement);
    });
  }

  // NEW: replaces the old redirect-on-model behaviour.
  // Fetches the selected product and lists its variant titles in the dropdown.
  async function fillListVariants({ handle }) {
    const requestId = ++activeVariantRequest;

    clearDD();
    setSearchBarVisible(false);
    appendSpinnerItem();

    const product = await fetchProductVariants(handle);

    // A newer selection happened while we were fetching -> drop this result.
    if (requestId !== activeVariantRequest) return;

    clearDD();

    const allVariants = product?.variants?.nodes ?? [];

    if (availablePcCodesRaw === undefined) {
      console.warn(
        "data-available-pc-codes is not set; skipping PC-code filtering."
      );
    }

    const variants = allVariants.filter((variant) => {
      const plug = variant.title.split("/")[0].trim(); // e.g. "PC10"
      const plugClean = plug.split("-")[0].trim(); // strip "-x" suffix

      // 1) Must be one of low_product's available PC codes (when provided).
      if (availablePcCodesRaw !== undefined && !availablePcCodes.has(plug)) {
        return false;
      }

      // 2) Exclude anti-theft variants (PC code is an anti-theft socket).
      if (proSockets.has(plugClean)) {
        return false;
      }

      return true;
    });

    // Product exists but no variant survived the filter (e.g. Tacoma: every
    // variant is a pro-socket and/or not in low_product). Send the user to the
    // product page so they can pick from all generations there.
    if (!variants.length && allVariants.length) {
      redirectToProduct(handle);
      return;
    }

    // Genuinely nothing to show (product not found / no variants at all).
    if (!variants.length) {
      const li = getListItem({ text: noVehicleFoundText });
      ddList.append(li);
      ddList.style.height = "120px";
      return;
    }

    setListHeight(variants.length);

    variants.forEach((variant) => {
      const li = getVariantListItem({ variant, handle });
      ddList.append(li);
    });
  }

  function clearDD() {
    const totalChildren = ddList.children.length;
    for (let i = totalChildren - 1; i > 0; i--) {
      ddList.removeChild(ddList.children[i]);
    }
    // After any selection / rebuild, scroll back to the top so the user
    // doesn't stay scrolled past the first items of the new list.
    ddList.scrollTop = 0;
  }

  function setListHeight(count) {
    if (count < 5) {
      ddList.style.height = 68 + count * 52 + "px";
    } else {
      ddList.style.height = "";
    }
  }

  function appendSearchBar() {
    const li = document.createElement("li");
    li.classList.add("pc-pro-dd-list-item", "pc-pro-dd-search-bar-li");
    const input = document.createElement("input");
    input.type = "text";
    input.classList.add("pc-pro-search-bar");
    input.addEventListener("input", handleSearchBarChange);
    input.placeholder = "Search";
    li.append(input);
    ddList.append(li);
  }

  function handleSearchBarChange(e) {
    const searchValue = e.target.value.toLowerCase();
    ddList.style.height = "";

    clearDD();
    if (searchValue === "") {
      getInitialDropdown({ addSearchBar: false });
      return;
    }
    const foundArray = [];
    Object.keys(makeModels).forEach((make) => {
      const models = makeModels[make];

      if (make.toLowerCase().includes(searchValue)) {
        foundArray.push({ type: "make", name: make });
      }

      models.forEach((model) => {
        if (model.toLowerCase().includes(searchValue)) {
          // Keep make/model separate so we can build the breadcrumb correctly.
          foundArray.push({
            type: "model",
            name: `${make} ${model}`,
            make,
            model,
          });
        }
      });
    });

    if (foundArray.length < 5) {
      const newHeight = 68 + foundArray.length * 52 + "px";
      ddList.style.height = newHeight;
    }

    if (foundArray.length < 1) {
      const listItem = getListItem({ text: noVehicleFoundText });
      ddList.append(listItem);
      ddList.style.height = "120px";
      return;
    }

    foundArray.sort((a, b) => {
      if (a.type === "make" && b.type === "model") return -1;
      if (a.type === "model" && b.type === "make") return 1;
      return 0;
    });
    foundArray.forEach((item) => {
      if (item.type === "make") {
        const makeLi = getMakeListItem({ make: item.name });
        ddList.append(makeLi);
      } else {
        const modelLi = getModelListItem({
          label: item.name,
          handle: toHandle(item.name),
          make: item.make,
          model: item.model,
        });
        ddList.append(modelLi);
      }
    });
  }

  function getSplitter({ text }) {
    const li = document.createElement("li");
    li.classList.add("pc-pro-dd-list-item", "pc-pro-dd-list-splitter");
    li.innerText = text;
    return li;
  }

  function getMakeListItem({ make }) {
    const li = document.createElement("li");
    li.classList.add("pc-pro-dd-list-item");
    li.innerText = make;
    li.addEventListener("click", () => {
      showMakeSelection(make);
      setListHeight(makeModels[make].length);
      fillListModels(make);
      document.querySelector(".pc-pro-search-bar").value = "";
    });
    addHighlightEventListeners(li);
    return li;
  }

  // CHANGED: no longer redirects. Loads the product's variants into the list.
  function getModelListItem({ label, handle, make, model }) {
    const li = document.createElement("li");
    li.classList.add("pc-pro-dd-list-item");
    li.innerText = label;
    li.addEventListener("click", async () => {
      if (make && model) {
        showMakeModelSelection(make, model);
      } else {
        // Fallback (shouldn't normally happen): single combined chip.
        showSingleSelection(label);
      }
      document.querySelector(".pc-pro-search-bar").value = "";
      await fillListVariants({ handle });
    });
    addHighlightEventListeners(li);
    return li;
  }

  // NEW: the variant is the final, clickable step that goes to the product page.
  function getVariantListItem({ variant, handle }) {
    const li = document.createElement("li");
    li.classList.add("pc-pro-dd-list-item");
    li.innerText = formatVariantTitle(variant.title);
    li.addEventListener("click", () => {
      redirectToVariant(handle, variant.id);
    });
    addHighlightEventListeners(li);
    return li;
  }

  function addHighlightEventListeners(el) {
    el.addEventListener("mouseenter", () => {
      const isKeyboardMode = ddContent.dataset.keyboardControls === "true";
      if (!isKeyboardMode) {
        el.classList.add("highlight");
      }
    });
    el.addEventListener("mouseleave", () => {
      const isKeyboardMode = ddContent.dataset.keyboardControls === "true";
      if (!isKeyboardMode) {
        el.classList.remove("highlight");
      }
    });
  }

  function getListItem({ text }) {
    const li = document.createElement("li");
    li.classList.add("pc-pro-dd-list-item", "no-hover");
    li.innerText = text;
    return li;
  }

  // Toggles the persistent search bar <li> (hidden in the variant stage).
  function setSearchBarVisible(visible) {
    const li = ddList.querySelector(".pc-pro-dd-search-bar-li");
    if (li) li.style.display = visible ? "" : "none";
  }

  // Centered spinner row used for in-list loading (fetch + redirect stages).
  function appendSpinnerItem() {
    const li = document.createElement("li");
    li.classList.add("pc-pro-dd-list-item", "no-hover");
    li.style.display = "flex";
    li.style.justifyContent = "center";
    li.style.alignItems = "center";
    const spinner = document.createElement("span");
    spinner.className = "pc-pro-spinner";
    li.appendChild(spinner);
    ddList.append(li);
    ddList.style.height = "120px";
    return li;
  }

  // Clears the list, hides the search bar and shows a spinner while the page
  // navigates to a product / variant URL.
function showRedirectLoading(message = "") {
  clearDD();
  setSearchBarVisible(false);

  const li = appendSpinnerItem();

  if (message) {
    li.style.flexDirection = "column";
    li.style.gap = "10px";
    li.style.textAlign = "center";

    const text = document.createElement("p");
    text.innerText = message;
    text.style.margin = "0";
    text.style.fontSize = "14px";
    text.style.lineHeight = "1.4";

    li.appendChild(text);
  }
}

  // Initial loading: hides the dropdown UI and shows a spinner until the
  // make/model data is ready (see the :not(.pc-pro-ready) CSS rules below).
  function injectPcProStyles() {
    if (document.getElementById("pc-pro-inline-styles")) return;
    const style = document.createElement("style");
    style.id = "pc-pro-inline-styles";
    style.textContent = `
      .pc-pro-dropdown-content:not(.pc-pro-ready) .dropdown-button,
      .pc-pro-dropdown-content:not(.pc-pro-ready) .pc-pro-dd-list,
      .pc-pro-dropdown-content:not(.pc-pro-ready) .pc-pro-selection-container {
        display: none !important;
      }
      .pc-pro-initial-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1.25rem;
        min-height: 56px;
      }
      .pc-pro-dropdown-content.pc-pro-ready .pc-pro-initial-loading {
        display: none;
      }
      .pc-pro-spinner {
        width: 24px;
        height: 24px;
        border: 3px solid rgba(0, 0, 0, 0.15);
        border-top-color: rgba(0, 0, 0, 0.6);
        border-radius: 50%;
        animation: pcProSpin 0.7s linear infinite;
        flex: 0 0 auto;
      }
      @keyframes pcProSpin { to { transform: rotate(360deg); } }
    `;
    document.head.appendChild(style);
  }

  function showInitialLoading() {
    if (ddContent.querySelector(".pc-pro-initial-loading")) return;
    const wrap = document.createElement("div");
    wrap.className = "pc-pro-initial-loading";
    const spinner = document.createElement("span");
    spinner.className = "pc-pro-spinner";
    wrap.appendChild(spinner);
    ddContent.appendChild(wrap);
  }

  function markReady() {
    ddContent.classList.add("pc-pro-ready");
    ddContent.querySelector(".pc-pro-initial-loading")?.remove();
  }

  function toHandle(makeModel) {
    return makeModel.toLowerCase().replaceAll(" ", "-");
  }

  // Variant title is "PC.. / make / model / year / engine".
  // Show the meaningful part (year — engine). Return full title if it doesn't match.
  // To list the RAW title instead, just `return title;` here.
  function formatVariantTitle(title) {
    const parts = title.split("/").map((p) => p.trim());
    const detail = parts.slice(3).filter(Boolean).join(" — ");
    return detail || title;
  }

  function redirectToVariant(handle, variantGid) {
    showRedirectLoading();
    const variantId = variantGid.split("/").pop();
    window.location = `${window.Shopify.routes.root}products/${handle}?variant=${variantId}`;
  }

  // Used when a model has no matching without-anti-theft variant (e.g. Tacoma):
  // go to the product page with no variant preselected so the user can choose.
function redirectToProduct(handle) {
  showRedirectLoading(
    "Anti-Theft is available for your selection. Redirecting to the product page for details..."
  );

  window.location = `${window.Shopify.routes.root}products/${handle}`;
}

  // --- Selection chips / breadcrumb -----------------------------------------

  function removeSelectionItems() {
    ddSelections.innerHTML = "";
  }

  function removeSelectionItem(div) {
    div.classList.remove("visible");
    setTimeout(() => div.remove(), 200);
  }

  function showMakeSelection(make) {
    removeSelectionItems();
    createSelectionItem({
      text: make,
      onDelete: ({ element }) => {
        removeChipAndFollowing(element);
        resetToInitial();
      },
    });
  }

  function showMakeModelSelection(make, model) {
    removeSelectionItems();
    createSelectionItem({
      text: make,
      onDelete: ({ element }) => {
        removeChipAndFollowing(element);
        resetToInitial();
      },
    });
    createSelectionItem({
      text: model,
      onDelete: ({ element }) => {
        removeSelectionItem(element);
        setListHeight(makeModels[make].length);
        fillListModels(make);
      },
    });
  }

  function showSingleSelection(text) {
    removeSelectionItems();
    createSelectionItem({
      text,
      onDelete: ({ element }) => {
        removeSelectionItem(element);
        resetToInitial();
      },
    });
  }

  function removeChipAndFollowing(element) {
    let next = element.nextElementSibling;
    removeSelectionItem(element);
    while (next) {
      const toRemove = next;
      next = next.nextElementSibling;
      removeSelectionItem(toRemove);
    }
  }

  function resetToInitial() {
    clearDD();
    getInitialDropdown({ addSearchBar: false });
  }

  function createSelectionItem({ text, onDelete }) {
    const div = document.createElement("div");
    const cross = document.createElement("button");
    cross.classList.add("pc-pro-selection-delete");
    cross.addEventListener("click", () => {
      if (typeof onDelete === "function") {
        onDelete({ element: div });
      } else {
        removeSelectionItem(div);
        resetToInitial();
      }
    });
    const textEl = document.createElement("p");
    div.classList.add("pc-pro-selection-item");
    textEl.innerText = text;
    div.append(textEl, cross);
    ddSelections.append(div);
    setTimeout(() => {
      div.classList.add("visible");
    }, 20);
  }

  // --- Data fetching ---------------------------------------------------------

  async function fetchAllProducts() {
    let hasNextPage = true;
    let endCursor = null;
    let allVariants = [];
    const endpoint = `/api/2024-07/graphql`;
    const headers = {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": "960b68e3d27e55870e9ad333d5a199a1",
    };
    const fetchProducts = async (cursor = null) => {
      const query = `
      {
        collectionByHandle(handle: "${collectionHandle}") {
          products(first: 250${cursor ? `, after: "${cursor}"` : ""},) {
            pageInfo {
              hasNextPage
              endCursor
            }
            nodes {
              vendor
              variants(first: 1) {
                nodes {
                  title
                }
              }
            }
          }
        }
      }
      `;
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: headers,
          body: JSON.stringify({ query }),
        });

        const data = await response.json();
        return data.data.collectionByHandle.products;
      } catch (error) {
        console.error("Error fetching GraphQL data:", error);
      }
    };

    while (hasNextPage) {
      const productsData = await fetchProducts(endCursor);
      allVariants = allVariants.concat(
        productsData.nodes.map((node) => node.variants.nodes[0].title)
      );
      hasNextPage = productsData.pageInfo.hasNextPage;
      endCursor = productsData.pageInfo.endCursor;
    }
    return allVariants;
  }

  // NEW: fetch all variants for a single product by handle.
  async function fetchProductVariants(handle) {
    const endpoint = `/api/2024-07/graphql`;
    const headers = {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": "960b68e3d27e55870e9ad333d5a199a1",
    };
    const query = `
    {
      productByHandle(handle: "${handle}") {
        handle
        variants(first: 100) {
          nodes {
            id
            title
            availableForSale
          }
        }
      }
    }
    `;
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ query }),
      });
      const data = await response.json();
      return data?.data?.productByHandle ?? null;
    } catch (error) {
      console.error("Error fetching product variants:", error);
      return null;
    }
  }

  async function getMakeModels() {
    const allVariants = await fetchAllProducts();

    const makes = {};
    allVariants.forEach((variant) => {
      const [pcCode, make, model, year, engine] = variant
        .split("/")
        .map((item) => item.trim());
      if (!pcCode.startsWith("PC")) return;
      if (!makes[make]) {
        makes[make] = [model];
      } else {
        makes[make].push(model);
      }
    });
    const orderedMakes = {};
    Object.keys(makes).map((key) => {
      const orderedModels = makes[key].sort();
      orderedMakes[key] = orderedModels;
    });
    return orderedMakes;
  }
  // FUNCTIONS END
});

function redirectPPFunc(id, href, pc, year, make, model) {
  window.localStorage.setItem(
    "hpValues",
    JSON.stringify({ id, selection: `${pc} / ${year}` })
  );
  window.location.href = href;
}